import { useEffect, useState } from "react";
import { useSnapshot } from "../contexts/SnapshotContext";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import {
  listVentajas,
  createVentaja,
  updateVentaja,
  deleteVentaja,
  ventajaProgreso,
} from "../lib/api";
import { formatEUR, formatNum, RANK_LABELS } from "../lib/format";
import { RankBadge } from "../components/RankBadge";
import {
  Gift,
  Plus,
  Trash,
  Trophy,
  CheckCircle,
  XCircle,
  X,
  CaretDown,
  CaretRight,
  Calendar,
  PencilSimple,
  Warning,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const RANKS = [
  { value: "Consultor", label: "Consultoras" },
  { value: "Lider Junior", label: "Líderes Júnior" },
  { value: "Lider Ejecutivo", label: "Líderes Ejecutivas" },
  { value: "Lider Master", label: "Líderes Master" },
];

const METRICS = [
  { value: "vendas_pessoais", label: "Ventas personales (€)", unit: "€" },
  { value: "vendas_equipa", label: "Ventas equipo (€)", unit: "€" },
  { value: "diretos_ativos", label: "Directas activas", unit: "" },
  { value: "altas_personales", label: "Altas personales (directas)", unit: "" },
  { value: "altas_equipo", label: "Altas de equipo (toda la línea)", unit: "" },
  { value: "junior_lideres_equipa", label: "Júnior en equipo", unit: "" },
];

function emptyPrize() {
  return { id: crypto.randomUUID().replace(/-/g, "").slice(0, 8), name: "", description: "", start_date: "", end_date: "", eligible_ranks: null, requirements: [{ metric: "vendas_pessoais", target: 200, label: "", operator: "and" }] };
}

function ProgressMini({ value, met }) {
  const c = met ? "bg-[#8A9A86]" : value >= 60 ? "bg-[#E3A569]" : "bg-[#C57B67]";
  return (
    <div className="h-1.5 w-full bg-[#F2EFEA] rounded-full overflow-hidden">
      <div className={`h-full ${c}`} style={{ width: `${Math.min(100, value)}%` }} />
    </div>
  );
}

function PrizeBlock({ prize, onChange, onRemove }) {
  const setField = (k, v) => onChange({ ...prize, [k]: v });
  const addReq = () =>
    setField("requirements", [...prize.requirements, { metric: "vendas_pessoais", target: 100, label: "", operator: "and" }]);
  const updateReq = (i, r) => {
    const arr = [...prize.requirements];
    arr[i] = r;
    setField("requirements", arr);
  };
  const removeReq = (i) => setField("requirements", prize.requirements.filter((_, idx) => idx !== i));

  return (
    <div className="border border-[#E5E0D8] rounded-lg p-4 space-y-3 bg-[#F9F8F6]/50">
      <div className="flex items-start gap-3">
        <Trophy size={20} weight="duotone" className="text-[#E3A569] mt-1" />
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            placeholder="Nombre del premio (ej. Set cocina)"
            value={prize.name}
            onChange={(e) => setField("name", e.target.value)}
            data-testid="prize-name"
          />
          <Input
            placeholder="Descripción opcional"
            value={prize.description || ""}
            onChange={(e) => setField("description", e.target.value)}
          />
        </div>
        <button
          onClick={onRemove}
          className="p-1.5 text-[#736F68] hover:text-[#B85C5C] hover:bg-[#B85C5C]/10 rounded-lg"
        >
          <X size={16} />
        </button>
      </div>
      {/* Optional sub-period within the month */}
      <div className="pl-9 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#736F68] font-semibold">
            Desde (opcional)
          </Label>
          <Input
            type="date"
            value={prize.start_date || ""}
            onChange={(e) => setField("start_date", e.target.value)}
            data-testid="prize-start-date"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#736F68] font-semibold">
            Hasta (opcional)
          </Label>
          <Input
            type="date"
            value={prize.end_date || ""}
            onChange={(e) => setField("end_date", e.target.value)}
            data-testid="prize-end-date"
          />
        </div>
      </div>
      {(prize.start_date || prize.end_date) && (
        <p className="pl-9 text-[11px] text-[#C57B67]">
          Tramo acotado: ventas y altas se calculan <strong>solo dentro de este periodo</strong>.
          {prize.start_date && prize.start_date.slice(8) !== "01" && (
            <> Para delimitar ventas desde mitad de mes necesitas tener subido un CSV del día anterior al inicio.</>
          )}
        </p>
      )}
      <div className="pl-9 space-y-1.5">
        <p className="text-[10px] uppercase tracking-wider text-[#736F68] font-semibold">
          Dirigido a (vacío = todos los rangos)
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {RANKS.map((rank) => {
            const checked = (prize.eligible_ranks || []).includes(rank.value);
            return (
              <label key={rank.value} className="flex items-center gap-1.5 text-xs text-[#2D2A26] cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="accent-[#C57B67]"
                  checked={checked}
                  onChange={(e) => {
                    const current = prize.eligible_ranks || [];
                    const next = e.target.checked
                      ? [...current, rank.value]
                      : current.filter((r) => r !== rank.value);
                    setField("eligible_ranks", next.length ? next : null);
                  }}
                />
                {rank.label}
              </label>
            );
          })}
        </div>
      </div>
      <div className="space-y-2 pl-9">
        <p className="text-[10px] uppercase tracking-wider text-[#736F68] font-semibold">
          Requisitos
        </p>
        {prize.requirements.map((r, i) => (
          <div key={i} className="space-y-2">
            {i > 0 && (
              <div className="flex items-center gap-1">
                {["and", "or"].map((op) => {
                  const active = (r.operator || "and") === op;
                  return (
                    <button
                      key={op}
                      onClick={() => updateReq(i, { ...r, operator: op })}
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border transition-colors ${
                        active
                          ? "bg-[#C57B67] border-[#C57B67] text-white"
                          : "border-[#E5E0D8] text-[#736F68] hover:border-[#C57B67] hover:text-[#C57B67]"
                      }`}
                      data-testid={`req-operator-${op}-${i}`}
                    >
                      {op === "and" ? "Y" : "O"}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Select value={r.metric} onValueChange={(v) => updateReq(i, { ...r, metric: v })}>
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METRICS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-[#736F68] text-sm">≥</span>
              <Input
                type="number"
                step="0.01"
                value={r.target}
                onChange={(e) => updateReq(i, { ...r, target: Number(e.target.value) })}
                className="w-28"
                data-testid="prize-req-target"
              />
              {prize.requirements.length > 1 && (
                <button
                  onClick={() => removeReq(i)}
                  className="p-1.5 text-[#736F68] hover:text-[#B85C5C]"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
        {prize.requirements.some((r, i) => i > 0 && r.operator === "or") && (
          <p className="text-[11px] text-[#736F68]">
            Los requisitos unidos con <strong>O</strong> forman un grupo: basta cumplir uno de ellos. Los grupos se combinan con <strong>Y</strong>.
          </p>
        )}
        <button
          onClick={addReq}
          className="text-xs text-[#C57B67] hover:underline mt-1"
          data-testid="add-req-btn"
        >
          + Añadir requisito
        </button>
      </div>
    </div>
  );
}

function ReqBar({ req }) {
  const pct = Math.min(100, Math.round(req.progress));
  const color = req.met ? "bg-[#8A9A86]" : pct >= 60 ? "bg-[#E3A569]" : "bg-[#C57B67]";
  const metricLabel = METRICS.find((m) => m.value === req.metric)?.label ?? req.metric;
  const unit = METRICS.find((m) => m.value === req.metric)?.unit ?? "";
  const fmt = (v) => (unit === "€" ? formatEUR(v) : formatNum(v));
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-[#736F68] truncate">{metricLabel}</span>
        <span className={`font-mono font-semibold ml-2 shrink-0 ${req.met ? "text-[#8A9A86]" : "text-[#736F68]"}`}>
          {fmt(req.current)} / {fmt(req.target)}
        </span>
      </div>
      <div className="h-1.5 w-full bg-[#F2EFEA] rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ConsultorRow({ row, prizes, expanded, onToggle }) {
  return (
    <>
      <tr
        onClick={onToggle}
        className="hover:bg-[#F9F8F6] cursor-pointer select-none"
        data-testid={`progress-row-${row.id_consultor}`}
      >
        <td className="px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="text-[#736F68]">
              {expanded ? <CaretDown size={13} /> : <CaretRight size={13} />}
            </span>
            <div>
              <p className="font-medium text-[#2D2A26]">{row.nome}</p>
              <p className="text-[10px] text-[#736F68]">{row.titulo}</p>
            </div>
          </div>
        </td>
        {row.evaluaciones.map((e) => {
          if (e.eligible === false) {
            return (
              <td key={e.prize_id} className="px-3 py-2.5 text-center">
                <span className="text-[#C5C0B8] text-xs">—</span>
              </td>
            );
          }
          // Progreso por grupos: dentro de un grupo O cuenta el requisito más avanzado
          const groups = [];
          e.requirements.forEach((r) => {
            if (groups.length && r.operator === "or") groups[groups.length - 1].push(r);
            else groups.push([r]);
          });
          const avg = groups.length > 0
            ? Math.round(groups.reduce((s, g) => s + Math.max(...g.map((r) => r.progress)), 0) / groups.length)
            : 0;
          return (
            <td key={e.prize_id} className="px-3 py-2.5 text-center">
              <div className="flex flex-col items-center gap-1">
                {e.all_met ? (
                  <CheckCircle size={18} weight="fill" className="text-[#8A9A86]" />
                ) : (
                  <span className="text-xs font-mono text-[#736F68]">{avg}%</span>
                )}
                <div className="w-16">
                  <ProgressMini value={avg} met={e.all_met} />
                </div>
              </div>
            </td>
          );
        })}
      </tr>
      {expanded && (
        <tr>
          <td colSpan={1 + prizes.length} className="px-4 pb-3 pt-0 bg-[#F9F8F6]">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
              {row.evaluaciones.map((e) => {
                const prize = prizes.find((p) => p.id === e.prize_id);
                if (e.eligible === false) {
                  return (
                    <div
                      key={e.prize_id}
                      className="rounded-lg border border-[#E5E0D8] p-3 space-y-1 bg-white opacity-40"
                    >
                      <div className="flex items-center gap-2">
                        <XCircle size={15} weight="fill" className="text-[#C5C0B8] shrink-0" />
                        <p className="text-xs font-semibold text-[#736F68] truncate">
                          {prize?.name ?? e.prize_id}
                        </p>
                      </div>
                      <p className="text-[10px] text-[#C5C0B8]">No aplica a este rango</p>
                    </div>
                  );
                }
                return (
                  <div
                    key={e.prize_id}
                    className={`rounded-lg border p-3 space-y-2 bg-white ${e.all_met ? "border-[#8A9A86]" : "border-[#E5E0D8]"}`}
                  >
                    <div className="flex items-center gap-2">
                      {e.all_met ? (
                        <CheckCircle size={15} weight="fill" className="text-[#8A9A86] shrink-0" />
                      ) : (
                        <XCircle size={15} weight="fill" className="text-[#C57B67] shrink-0" />
                      )}
                      <p className="text-xs font-semibold text-[#2D2A26] truncate">
                        {prize?.name ?? e.prize_id}
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      {e.requirements.map((r, i) => (
                        <div key={i} className="space-y-1.5">
                          {i > 0 && r.operator === "or" && (
                            <p className="text-[9px] uppercase tracking-wider text-[#E3A569] font-bold">
                              — o bien —
                            </p>
                          )}
                          <ReqBar req={r} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ConsultorTable({ consultores, prizes }) {
  const [expandedId, setExpandedId] = useState(null);
  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F8F6] text-[10px] uppercase tracking-wider text-[#736F68]">
            <tr className="text-left">
              <th className="px-4 py-2 font-semibold">Consultora</th>
              {prizes.map((pr) => (
                <th key={pr.id} className="px-3 py-2 font-semibold text-center">
                  {pr.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F2EFEA]">
            {consultores.map((row) => (
              <ConsultorRow
                key={row.id_consultor}
                row={row}
                prizes={prizes}
                expanded={expandedId === row.id_consultor}
                onToggle={() => toggle(row.id_consultor)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProgramCard({ prog, onDelete, onEdit, snapshotId, canEdit, canDelete }) {
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(null);

  const loadProgress = async () => {
    const r = await ventajaProgreso(prog.id, snapshotId);
    setProgress(r);
  };

  // Reset progress when program or snapshot changes
  useEffect(() => {
    setProgress(null);
  }, [prog, snapshotId]);

  // Load progress when open is true and progress is null
  useEffect(() => {
    if (open && !progress) {
      loadProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, progress]);

  const toggle = () => {
    setOpen(!open);
  };

  return (
    <div className="bg-white border border-[#E5E0D8] rounded-xl overflow-hidden" data-testid={`program-${prog.id}`}>
      <div onClick={toggle} className="w-full p-5 cursor-pointer text-left flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#E3A569]/15 flex items-center justify-center">
          <Gift size={22} weight="duotone" className="text-[#E3A569]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#2D2A26]">{prog.name}</p>
          <p className="text-xs text-[#736F68] mt-0.5">
            Periodo {prog.period} · {prog.prizes.length} premios
            {prog.description && ` · ${prog.description}`}
          </p>
        </div>
        {canEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(prog);
            }}
            className="p-2 rounded-lg text-[#736F68] hover:text-[#C57B67] hover:bg-[#F2EFEA]"
            data-testid={`edit-program-${prog.id}`}
          >
            <PencilSimple size={16} />
          </button>
        )}
        {canDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`¿Eliminar el programa "${prog.name}"?`)) onDelete(prog.id);
            }}
            className="p-2 rounded-lg text-[#736F68] hover:text-[#B85C5C] hover:bg-[#B85C5C]/10"
            data-testid={`delete-program-${prog.id}`}
          >
            <Trash size={16} />
          </button>
        )}
        <span className="text-[#736F68]">{open ? <CaretDown size={16} /> : <CaretRight size={16} />}</span>
      </div>
      {open && (
        <div className="border-t border-[#F2EFEA] p-5 space-y-4 bg-[#F9F8F6]/40">
          {/* Summary per prize */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {prog.prizes.map((pr) => {
              const winners = progress?.summary?.ganadores_por_premio?.[pr.id] ?? "—";
              const prizeCtx = progress?.summary?.contexto_por_premio?.[pr.id];
              const isRange = prizeCtx?.mode === "range";
              const missingBaseline = isRange && !prizeCtx?.baseline_snapshot_date && pr.start_date && pr.start_date.slice(8) !== "01";
              const fmtDate = (d) => d ? d.slice(5).replace("-", "/") : null;
              return (
                <div key={pr.id} className={`bg-white border rounded-lg p-3 ${missingBaseline ? "border-[#E3A569]" : "border-[#E5E0D8]"}`} data-testid={`prize-summary-${pr.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm text-[#2D2A26] truncate">{pr.name}</p>
                    <span className="text-[#8A9A86] font-mono text-sm font-semibold">
                      {winners} ✓
                    </span>
                  </div>

                  {/* Date range of the prize */}
                  {(pr.start_date || pr.end_date) && (
                    <p className="text-[10px] text-[#C57B67] mt-1 inline-flex items-center gap-1">
                      <Calendar size={10} weight="duotone" />
                      {fmtDate(pr.start_date) || "inicio"} → {fmtDate(pr.end_date) || "fin"}
                    </p>
                  )}

                  {/* Snapshot dates actually used for calculation */}
                  {prizeCtx && (
                    <p className="text-[10px] text-[#736F68] mt-0.5 flex items-center gap-1">
                      {isRange ? (
                        <>
                          CSV base:{" "}
                          <span className={missingBaseline ? "text-[#E3A569] font-semibold" : "font-medium"}>
                            {prizeCtx.baseline_snapshot_date ? fmtDate(prizeCtx.baseline_snapshot_date) : "—"}
                          </span>
                          {" · "}actual: <span className="font-medium">{fmtDate(prizeCtx.current_snapshot_date) || "—"}</span>
                        </>
                      ) : (
                        <>CSV: <span className="font-medium">{fmtDate(prizeCtx.current_snapshot_date) || "—"}</span></>
                      )}
                    </p>
                  )}

                  {/* Warning when baseline snapshot is missing for mid-month prizes */}
                  {missingBaseline && (
                    <p className="mt-1 text-[10px] text-[#E3A569] flex items-start gap-1">
                      <Warning size={11} weight="fill" className="mt-0.5 shrink-0" />
                      Sin CSV base anterior al {fmtDate(pr.start_date)} — las ventas muestran el acumulado del mes completo
                    </p>
                  )}

                  {pr.eligible_ranks && pr.eligible_ranks.length > 0 && (
                    <p className="text-[10px] text-[#8A9A86] mt-1 font-medium">
                      Para: {pr.eligible_ranks.map((r) => RANK_LABELS[r] || r).join(", ")}
                    </p>
                  )}
                  <ul className="mt-2 space-y-0.5 text-[11px] text-[#736F68]">
                    {pr.requirements.map((r, i) => (
                      <li key={i}>
                        {i === 0 ? "·" : r.operator === "or" ? (
                          <strong className="text-[#E3A569]">O</strong>
                        ) : (
                          <strong>Y</strong>
                        )}{" "}
                        {METRICS.find((m) => m.value === r.metric)?.label} ≥{" "}
                        {METRICS.find((m) => m.value === r.metric)?.unit === "€"
                          ? formatEUR(r.target)
                          : formatNum(r.target)}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Consultor list */}
          {progress?.consultores?.length > 0 && (
            <ConsultorTable consultores={progress.consultores} prizes={prog.prizes} />
          )}
        </div>
      )}
    </div>
  );
}

export default function Ventajas() {
  const { selected, selectedCoordinator, coordinators } = useSnapshot();
  const { user, isSuperadmin, isAdmin, isMarketing } = useAuth();
  const { t } = useLang();
  const needsPais = isSuperadmin || isMarketing; // admin lo tiene fijo, coordinador no lo pone
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(null);

  const canManageItem = (prog) => {
    if (isSuperadmin) return true;
    return prog.created_by_id === user?.id;
  };

  const load = async () => {
    setLoading(true);
    const activeCoord = (coordinators || []).find((c) => c.name === selectedCoordinator);
    const params = {};
    if (selected) params.snapshot_id = selected.id;
    if (activeCoord?.id !== undefined) params.coordenador_id = activeCoord.id;
    const r = await listVentajas(params);
    setItems(r.items || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [selected, selectedCoordinator, coordinators]); // eslint-disable-line react-hooks/exhaustive-deps

  const startCreate = () => {
    const today = new Date();
    const defaultPeriod = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    setEditingId(null);
    setForm({
      name: "",
      description: "",
      period: defaultPeriod,
      pais: isAdmin ? user?.pais : (isSuperadmin ? "es" : null),
      prizes: [emptyPrize()],
    });
    setDialogOpen(true);
  };

  const startEdit = (prog) => {
    setEditingId(prog.id);
    setForm({
      name: prog.name,
      description: prog.description || "",
      period: prog.period,
      pais: prog.pais || null,
      prizes: prog.prizes.map((p) => ({
        id: p.id,
        name: p.name,
        description: p.description || "",
        start_date: p.start_date || "",
        end_date: p.end_date || "",
        eligible_ranks: p.eligible_ranks || null,
        requirements: p.requirements.map((r) => ({ ...r })),
      })),
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.name) {
      toast.error(t("guardar"));
      return;
    }
    if (needsPais && !form.pais) {
      toast.error(t("error_pais_requerido"));
      return;
    }
    const cleanPrizes = form.prizes
      .filter((p) => p.name && p.requirements.length > 0)
      .map((p) => ({
        ...p,
        start_date: p.start_date || null,
        end_date: p.end_date || null,
        requirements: p.requirements.filter((r) => r.target > 0),
      }))
      .filter((p) => p.requirements.length > 0);
    if (cleanPrizes.length === 0) {
      toast.error("Añade al menos un premio con un requisito");
      return;
    }
    try {
      const payload = { ...form, prizes: cleanPrizes };
      if (editingId) {
        await updateVentaja(editingId, payload);
        toast.success("Programa actualizado");
      } else {
        await createVentaja(payload);
        toast.success("Programa creado");
      }
      setDialogOpen(false);
      setEditingId(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Error desconocido";
      toast.error(`${editingId ? "Error al actualizar" : "Error al crear"}: ${detail}`);
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteVentaja(id);
      toast.success("Programa eliminado");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Error desconocido";
      toast.error(`Error al eliminar: ${detail}`);
    }
  };

  const paisLabel = { es: t("pais_es"), pt: t("pais_pt") };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="ventajas-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs tracking-widest uppercase text-[#736F68] font-display font-semibold">
            Programas
          </p>
          <h1 className="mt-1 text-3xl md:text-4xl font-semibold font-display text-[#2D2A26]">
            {t("ventajas_titulo")}
          </h1>
          <p className="mt-1 text-[#736F68] text-sm">
            Crea premios parciales por venta personal, equipo o nuevas altas. Ve quién va ganando cada premio.
          </p>
        </div>
        <button
          onClick={startCreate}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#C57B67] hover:bg-[#B06A57] text-white text-sm font-medium"
          data-testid="create-program-btn"
        >
          <Plus size={16} weight="bold" />
          {t("crear_programa")}
        </button>
      </header>

      {loading ? (
        <p className="text-[#736F68]">{t("cargando")}</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-10 text-center">
          <Gift size={40} weight="duotone" className="mx-auto text-[#E3A569]" />
          <p className="mt-3 font-semibold text-[#2D2A26]">{t("sin_ventajas")}</p>
          <p className="text-sm text-[#736F68] mt-1">
            Pulsa "{t("crear_programa")}" para definir premios y requisitos del mes.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <ProgramCard
              key={p.id}
              prog={p}
              onDelete={onDelete}
              onEdit={startEdit}
              snapshotId={selected?.id}
              canEdit={canManageItem(p)}
              canDelete={canManageItem(p)}
            />
          ))}
        </div>
      )}

      {/* Create/Edit dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => {
          if (!o) { setDialogOpen(false); setEditingId(null); }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-program-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingId ? t("editar_programa") : t("crear_programa")}
            </DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <Label className="text-xs text-[#736F68]">{t("nombre_programa")}</Label>
                  <Input
                    placeholder="Ej. Ventajas Abril 2026"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="program-name"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#736F68]">{t("periodo_label")}</Label>
                  <Input
                    placeholder="2026-04"
                    value={form.period}
                    onChange={(e) => setForm({ ...form, period: e.target.value })}
                    data-testid="program-period"
                  />
                </div>
              </div>

              {/* País: selector para marketing/superadmin, fijo para admin, oculto para coordinador */}
              {(needsPais || isAdmin) && (
                <div>
                  <Label className="text-xs text-[#736F68]">{t("pais_programa")}</Label>
                  {isAdmin ? (
                    <p className="text-sm text-[#2D2A26] mt-1 px-2.5 py-2 bg-[#F9F8F6] border border-[#E5E0D8] rounded-lg">
                      {paisLabel[user?.pais] || user?.pais}
                    </p>
                  ) : (
                    <select
                      value={form.pais || ""}
                      onChange={(e) => setForm({ ...form, pais: e.target.value || null })}
                      className="w-full text-sm border border-[#E5E0D8] rounded-lg px-2.5 py-2 bg-white text-[#2D2A26] focus:outline-none focus:ring-1 focus:ring-[#C57B67] mt-1"
                      data-testid="program-pais"
                    >
                      <option value="">— {t("pais_label")} —</option>
                      <option value="es">{t("pais_es")}</option>
                      <option value="pt">{t("pais_pt")}</option>
                    </select>
                  )}
                </div>
              )}

              <div>
                <Label className="text-xs text-[#736F68]">{t("descripcion")}</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="space-y-3">
                <p className="text-xs uppercase tracking-wider text-[#736F68] font-semibold">
                  {t("premios")}
                </p>
                {form.prizes.map((p, i) => (
                  <PrizeBlock
                    key={i}
                    prize={p}
                    onChange={(np) => {
                      const arr = [...form.prizes];
                      arr[i] = np;
                      setForm({ ...form, prizes: arr });
                    }}
                    onRemove={() => setForm({ ...form, prizes: form.prizes.filter((_, idx) => idx !== i) })}
                  />
                ))}
                <button
                  onClick={() => setForm({ ...form, prizes: [...form.prizes, emptyPrize()] })}
                  className="text-sm text-[#C57B67] hover:underline"
                  data-testid="add-prize-btn"
                >
                  + {t("anadir_premio")}
                </button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setDialogOpen(false); setEditingId(null); }}
            >
              {t("cancelar")}
            </Button>
            <Button onClick={submit} className="bg-[#C57B67] hover:bg-[#B06A57] text-white" data-testid="save-program-btn">
              {editingId ? t("guardar_cambios") : t("crear_programa")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}